import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  useTheme, // Import useTheme
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import withAuth from "../../../../../../Hoc/withAuth";
import AlertPopup from "../../../../../../Components/Bits/AlertPopup";

const getFilenameFromContentDisposition = (header) => {
  if (!header) return null;
  const match = header.match(/filename="?([^"]+)"?/i);
  return match ? match[1] : null;
};

const AttachedDocuments = ({ evaluation, getEvaluationByID, user }) => {
  const { id, evalID } = useParams();
  const theme = useTheme();

  const config = {
    headers: {
      Authorization: `Bearer ${user.token}`,
    },
  };

  const attached_documents = evaluation?.attached_documents || [];

  const [doc, setDoc] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);

  const createDocument = useMutation({
    mutationFn: async (data) => {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/QAInbound/admin/Evaluations/${id}/Evaluations/${evalID}`,
        data,
        config
      );
      return response.data;
    },
    onSuccess: () => {
      getEvaluationByID.refetch();
    },
    onError: (error) => {
      console.error("Error uploading document:", error);
    },
  });

  const handleSave = () => {
    if (doc.length === 0) return;
    const formData = new FormData();
    doc.forEach((file) => {
      formData.append("file", file);
    });
    createDocument.mutate(formData);
    setDoc([]);
  };

  const handleFileChange = (event) => {
    if (event.target.files) {
      const newRecordings = Array.from(event.target.files);
      setDoc(newRecordings);
    }
  };

  const handleDownloadClick = async (documentData) => {
    if (!documentData?.storage_id) {
      console.error("Missing document data or storage ID");
      return;
    }

    const { storage_id, name: defaultFilename } = documentData;
    setDownloadingId(storage_id);

    const downloadUrl = `${process.env.REACT_APP_API_URL}/QAInbound/admin/Evaluations/${id}/Evaluations/${evalID}/documents/${storage_id}`;

    try {
      const response = await axios.get(downloadUrl, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        responseType: "blob",
      });

      const contentDisposition = response.headers["content-disposition"];
      const filename =
        getFilenameFromContentDisposition(contentDisposition) ||
        defaultFilename ||
        `${storage_id}_download`;

      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/octet-stream",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Download failed:", error);
      if (error.response) {
        if (
          error.response.data instanceof Blob &&
          error.response.data.type.includes("json")
        ) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errData = JSON.parse(reader.result);
              console.error("Error response data:", errData);
            } catch (parseError) {
              console.error("Could not parse error response blob:", parseError);
            }
          };
          reader.onerror = () => {
            console.error("Could not read error response blob.");
          };
          reader.readAsText(error.response.data);
        } else {
          console.error("Error response:", error.response.data);
        }
      } else if (error.request) {
        console.error("Error request:", error.request);
      } else {
        console.error("Error message:", error.message);
      }
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{ display: "flex", flexDirection: "column", height: "100%" }}
    >
      {" "}
      <Typography
        sx={{ pt: 1, pl: 1, flexShrink: 0 }}
        variant="body1"
        gutterBottom
      >
        Attached Documents
      </Typography>
      <Divider sx={{ flexShrink: 0 }} />
      <CardContent
        sx={{
          flexGrow: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* --- Display Existing Documents Section --- */}
        {attached_documents.length > 0 ? (
          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              maxHeight: "100%",
              mb: 2,
              pr: 1,

              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: theme.palette.grey[200],
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: theme.palette.grey[400],
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: theme.palette.grey[500],
                },
              },
            }}
          >
            <Stack spacing={1}>
              {attached_documents.map((docData) => (
                <Card key={docData.storage_id} variant="outlined" sx={{ p: 1 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flexGrow: 1,
                        minWidth: 0,
                      }}
                      title={docData.name || "Unnamed Document"}
                    >
                      {docData.name || "Unnamed Document"}
                    </Typography>

                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleDownloadClick(docData)}
                      disabled={
                        downloadingId === docData.storage_id ||
                        !docData.storage_id
                      }
                      sx={{ flexShrink: 0 }}
                    >
                      {downloadingId === docData.storage_id ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        "Download"
                      )}
                    </Button>
                  </Stack>
                </Card>
              ))}
            </Stack>
          </Box>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              textAlign: "center",
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            No documents attached yet.
          </Typography>
        )}

        <AlertPopup
          open={createDocument.isError}
          message={`Error uploading document: ${
            createDocument.error?.message || "Please try again."
          }`}
          severity="error"
        />
        <AlertPopup
          open={createDocument.isSuccess}
          message="Document uploaded successfully"
          severity="success"
        />
      </CardContent>
      <Box
        sx={{
          p: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <input
          accept="*"
          id="contained-button-file"
          type="file"
          multiple
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        {doc.length === 0 ? (
          <label htmlFor="contained-button-file">
            <Button
              variant="outlined"
              fullWidth
              component="span"
              disabled={createDocument.isLoading}
            >
              Upload New Document(s)
            </Button>
          </label>
        ) : (
          <Box
            sx={{
              p: 1.5,
              borderColor: "primary.main",
              borderWidth: "1px",
              borderStyle: "dashed",
              borderRadius: 1,
              backgroundColor: theme.palette.action.hover,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              <Box sx={{ overflow: "hidden", flexGrow: 1 }}>
                <Typography
                  variant="body2"
                  noWrap
                  title={doc.map((file) => file.name).join(", ")}
                >
                  Selected: {doc.map((file) => file.name).join(", ")}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <Button
                  color="secondary"
                  variant="outlined"
                  size="small"
                  onClick={() => setDoc([])}
                  disabled={createDocument.isLoading}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  size="small"
                  onClick={handleSave}
                  disabled={createDocument.isLoading}
                  startIcon={
                    createDocument.isLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                >
                  {createDocument.isLoading ? "Saving..." : "Save Document(s)"}
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default withAuth(AttachedDocuments);
